import type { WidgetFieldSource } from "@/domains/buildings/types/buildingWidget";
import { canonicalAttributeValue } from "@/utils/attributeValue";

/**
 * Grouped non-user source. Multiple extractor entries sharing the same value
 * collapse into a single group whose `filenames` array carries every doc
 * the agent pulled that value from — same pattern the chat sidebar uses
 * via DocChipCollapsed for attached docs.
 *
 * `reasoning` carries the free-text explanation for the value: the extractor
 * agent's rationale, the IFC deterministic rule, or — for EHR — the
 * enum-normalisation note (WHA-3666), e.g. "EHR reported 'ühisveevärgist' →
 * mapped to 'Public grid'". Null when there is nothing to explain.
 *
 * An `ehr` group keeps its `sourceSystem` (free-text `api_building_data.
 * source_system`) because only Ehitisregister may be rendered as the EhrChip
 * deep link — see `isEhrRegistryName`. Drop it and every renderer credits
 * Ehitisregister for whatever registry actually reported the value.
 */
export type SourceGroup =
  | { kind: "ehr"; value: string; sourceSystem: string; reasoning: string | null }
  | { kind: "extractor"; value: string; filenames: string[]; reasoning: string | null }
  // WHA-3561: IFC (BIM-model) sources — grouped per value, one or more .ifc
  // filenames, rendered as a clickable doc chip (violet BIM-cube icon) that
  // opens the "Open 3D model?" confirm, same as a regular document source.
  | { kind: "ifc"; value: string; filenames: string[]; reasoning: string | null };

/**
 * One distinct value with its provenance: every source group that reported it,
 * whichever pipeline or registry that was. "9850" from a PDF and "9850.0" from
 * an IFC model are one option carrying both groups — see
 * `canonicalAttributeValue` for what counts as the same value. `value` is the
 * first group's spelling.
 */
export interface ValueOption {
  value: string;
  groups: SourceGroup[];
}

/**
 * The field's non-user sources as one option per distinct value, in the order
 * the values first appear in `sources` (the widget lists EHR, then documents,
 * then IFC).
 */
export function groupSourcesByValue(sources: WidgetFieldSource[]): ValueOption[] {
  const byValue = new Map<string, ValueOption>();
  for (const group of groupNonUserSources(sources)) {
    const key = canonicalAttributeValue(group.value);
    const existing = byValue.get(key);
    if (existing) existing.groups.push(group);
    else byValue.set(key, { value: group.value, groups: [group] });
  }
  return Array.from(byValue.values());
}

/**
 * Distinct non-user values other than the currently selected one, each with
 * its supporting source groups. A value reported by several source kinds
 * (e.g. EHR and a document) stays a single candidate carrying both groups.
 */
export function alternateCandidates(sources: WidgetFieldSource[], selected: string): ValueOption[] {
  const selectedKey = canonicalAttributeValue(selected);
  return groupSourcesByValue(sources).filter(
    (option) => canonicalAttributeValue(option.value) !== selectedKey
  );
}

export function groupNonUserSources(sources: WidgetFieldSource[]): SourceGroup[] {
  const byKey = new Map<string, SourceGroup>();
  for (const s of sources) {
    if (s.kind === "user") continue;
    // EHR rows are keyed by registry as well: two registries reporting the same
    // value are two sources, and merging them would label both with whichever
    // source system happened to come first.
    const value = canonicalAttributeValue(s.value);
    const key = s.kind === "ehr" ? `ehr::${s.sourceSystem}::${value}` : `${s.kind}::${value}`;
    if (s.kind === "ehr") {
      const existing = byKey.get(key);
      if (existing && existing.kind === "ehr") {
        if (!existing.reasoning && s.reasoning) existing.reasoning = s.reasoning;
      } else {
        byKey.set(key, {
          kind: "ehr",
          value: s.value,
          sourceSystem: s.sourceSystem,
          reasoning: s.reasoning,
        });
      }
    } else if (s.kind === "ifc") {
      const existing = byKey.get(key);
      if (existing && existing.kind === "ifc") {
        if (!existing.filenames.includes(s.filename)) existing.filenames.push(s.filename);
        if (!existing.reasoning && s.reasoning) existing.reasoning = s.reasoning;
      } else {
        byKey.set(key, {
          kind: "ifc",
          value: s.value,
          filenames: [s.filename],
          reasoning: s.reasoning,
        });
      }
    } else {
      const existing = byKey.get(key);
      if (existing && existing.kind === "extractor") {
        if (!existing.filenames.includes(s.filename)) existing.filenames.push(s.filename);
        if (!existing.reasoning && s.reasoning) existing.reasoning = s.reasoning;
      } else {
        byKey.set(key, {
          kind: "extractor",
          value: s.value,
          filenames: [s.filename],
          reasoning: s.reasoning,
        });
      }
    }
  }
  return Array.from(byKey.values());
}
